import React, { useRef, useLayoutEffect } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useTab } from '@atlaskit/tabs';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HorizontalNavItemLink = styled.a({
	display: 'inline-block',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'none',
	},
});

type TabItemProps = {
	label: string | null;
	isSelected: boolean;
	isDropdown: boolean;
	onClick: () => void;
	setWidth?: (width: number, index?: number) => void;
	href?: string;
	index?: number;
	size?: number;
};

export const TabItem = ({
	label,
	href,
	isSelected,
	isDropdown,
	onClick,
	index,
	setWidth,
	size,
}: TabItemProps) => {
	const ref = useRef<HTMLAnchorElement>(null);
	const { onKeyDown, tabIndex, ...tabAttributes } = useTab();
	const dropdownStyles = isDropdown
		? {
				color: isSelected ? token('color.text.selected') : token('color.text'),
				fontWeight: token('font.weight.medium'),
				fontFamily: token('font.family.body'),
				paddingBottom: token('space.075'),
				borderBottom: isSelected ? '2px solid' : '0px',
			}
		: null;

	const tabId = `space-settings-inner-nav-tabs-${index ? index + 1 : 1}`;

	const tabAttributesWithAccessibility = {
		...tabAttributes,
		id: tabId,
		'aria-controls': tabId,
		'aria-posinset': index ? index + 1 : 1,
		'aria-setsize': size,
	};

	useLayoutEffect(() => {
		if (setWidth && ref.current) {
			setWidth(ref.current.offsetWidth, index);
		}
	}, [setWidth, index]);

	return (
		<HorizontalNavItemLink
			href={href}
			{...tabAttributesWithAccessibility}
			aria-selected={isSelected}
			onClick={onClick}
			ref={ref}
			// Reset certain style properties of Atlaskit Tab that cause tabs to truncate text when
			// space is limited. Instead, we manually overflow tabs into a "More" dropdown menu.
			// Adding these styles in the styled.a component results in them being overridden by
			// Atlaskit; we must directly pass in style object to successfully override.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ ...dropdownStyles, overflow: 'visible', textOverflow: 'unset' }}
		>
			{label}
		</HorizontalNavItemLink>
	);
};
